/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { gql, type TypedDocumentNode } from '@apollo/client'
import { isServiceWorkerEnabled, SERVICE_WORKER_SCOPE } from '@web/src/lib/pwa/service-worker-registration'
import { print } from 'graphql'
import type {
  GetPushPublicKeyQuery,
  GetPushPublicKeyQueryVariables,
  RemovePushSubscriptionMutation,
  RemovePushSubscriptionMutationVariables,
  SavePushSubscriptionMutation,
  SavePushSubscriptionMutationVariables,
} from 'types/graphql'

const SIGN_OUT_CLEANUP_TIMEOUT_MS = 5000
const SIGNED_IN_MARKER_KEY = 'spicyfriends:signed-in'
const BASE64_PADDING_PATTERN = /=+$/

export const GET_PUSH_PUBLIC_KEY: TypedDocumentNode<GetPushPublicKeyQuery, GetPushPublicKeyQueryVariables> = gql`
  query GetPushPublicKey {
    getPushPublicKey {
      publicKey
    }
  }
`

export const SAVE_PUSH_SUBSCRIPTION: TypedDocumentNode<
  SavePushSubscriptionMutation,
  SavePushSubscriptionMutationVariables
> = gql`
  mutation SavePushSubscription($input: SavePushSubscriptionInput!) {
    savePushSubscription(input: $input)
  }
`

export const REMOVE_PUSH_SUBSCRIPTION: TypedDocumentNode<
  RemovePushSubscriptionMutation,
  RemovePushSubscriptionMutationVariables
> = gql`
  mutation RemovePushSubscription($input: RemovePushSubscriptionInput!) {
    removePushSubscription(input: $input)
  }
`

export type PushSubscriptionInput = {
  endpoint: string
  keys: {
    p256dh: string
    auth: string
  }
}

export function toPushSubscriptionInput(subscription: PushSubscription): PushSubscriptionInput {
  // Safari's toJSON() has returned standard base64, which the server rejects. getKey() is raw bytes.
  const p256dh = encodePushKey(subscription.getKey('p256dh')) ?? subscription.toJSON().keys?.p256dh
  const auth = encodePushKey(subscription.getKey('auth')) ?? subscription.toJSON().keys?.auth
  if (!(p256dh && auth)) {
    throw new Error('Failed to get subscription keys')
  }

  return {
    endpoint: subscription.endpoint,
    keys: { p256dh, auth },
  }
}

function encodePushKey(key: ArrayBuffer | null): string | null {
  if (!key) return null

  const bytes = new Uint8Array(key)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }

  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(BASE64_PADDING_PATTERN, '')
}

/**
 * Must run while the session is still valid: removes this browser's subscription from the
 * signed-in profile, unsubscribes the browser, and drops cached authenticated pages.
 * Never throws so sign-out always proceeds.
 */
export async function cleanUpPushOnSignOut(): Promise<void> {
  if (!isServiceWorkerEnabled()) {
    return
  }

  setSignedInMarker(false)

  try {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_SCOPE)
    if (!registration) {
      return
    }

    registration.active?.postMessage({ type: 'CLEAR_USER_DATA' })

    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      return
    }

    try {
      await fetch('/api/graphql', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: print(REMOVE_PUSH_SUBSCRIPTION),
          variables: { input: { subscription: toPushSubscriptionInput(subscription) } },
        }),
        signal: AbortSignal.timeout(SIGN_OUT_CLEANUP_TIMEOUT_MS),
      })
    } finally {
      // Unsubscribing makes the push service return 410, so the jobs worker drops any row left behind
      await subscription.unsubscribe()
    }
  } catch (error) {
    console.error('Error cleaning up push notifications on sign out:', error)
  }
}

/**
 * For sessions that ended without a sign-out (expiry, revocation, password change elsewhere).
 * The server row can no longer be removed without a session; unsubscribing makes the push
 * service return 410 so the jobs worker drops it.
 */
export async function cleanUpPushAfterSessionEnded(registration: ServiceWorkerRegistration): Promise<void> {
  setSignedInMarker(false)
  registration.active?.postMessage({ type: 'CLEAR_USER_DATA' })

  const subscription = await registration.pushManager.getSubscription()
  await subscription?.unsubscribe()
}

export function hasSignedInMarker(): boolean {
  try {
    return window.localStorage.getItem(SIGNED_IN_MARKER_KEY) === '1'
  } catch {
    return false
  }
}

export function setSignedInMarker(signedIn: boolean): void {
  try {
    if (signedIn) {
      window.localStorage.setItem(SIGNED_IN_MARKER_KEY, '1')
    } else {
      window.localStorage.removeItem(SIGNED_IN_MARKER_KEY)
    }
  } catch {
    // Storage can be unavailable (e.g. Safari private mode); the subscription check still applies
  }
}
